import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

const SESSION_KEY = 'clou_auth_session_v2';
const PERSISTENT_SESSION_KEY = 'clou_persistent_auth_session_v1';

export const isNativePlatform = () => {
  try {
    return Capacitor.isNativePlatform();
  } catch (e) {
    return false;
  }
};

export const isAppEnvironment = () => {
  return isNativePlatform() || window.matchMedia('(display-mode: standalone)').matches;
};

/**
 * Loads the current auth session.
 * - In Native App / App Environment: Always loads from localStorage/Preferences (never expires unless logged out).
 * - In Web Browser: Loads from sessionStorage (with 30-minute inactivity check).
 */
export const getStoredAuthSession = () => {
  try {
    const isApp = isAppEnvironment();

    // 1. Check Native / Persistent Storage first
    const persistentRaw = localStorage.getItem(PERSISTENT_SESSION_KEY);
    if (persistentRaw) {
      const parsed = JSON.parse(persistentRaw);
      if (parsed?.currentUser) {
        return {
          ...parsed,
          isPersistent: true
        };
      }
    }

    // 2. If running as native app, fallback to localStorage standard session
    if (isApp) {
      const localRaw = localStorage.getItem(SESSION_KEY);
      if (localRaw) {
        const parsed = JSON.parse(localRaw);
        if (parsed?.currentUser) {
          return {
            ...parsed,
            isPersistent: true
          };
        }
      }
    }

    // 3. Web Browser standard session (sessionStorage with 30-minute inactivity window)
    const sessionRaw = sessionStorage.getItem(SESSION_KEY);
    if (sessionRaw) {
      const parsed = JSON.parse(sessionRaw);
      const now = Date.now();
      const INACTIVITY_LIMIT_MS = 30 * 60 * 1000;
      if (parsed?.currentUser && parsed?.lastActivityAt && (now - parsed.lastActivityAt < INACTIVITY_LIMIT_MS)) {
        parsed.lastActivityAt = now;
        sessionStorage.setItem(SESSION_KEY, JSON.stringify(parsed));
        return {
          ...parsed,
          isPersistent: false
        };
      }
    }
  } catch (e) {
    console.warn('[StorageService] Error loading session:', e);
  }
  return null;
};

/**
 * Saves auth session state.
 * If in Native App or persistent is true, saves to localStorage & Preferences.
 * In Web Browser, saves to sessionStorage.
 */
export const saveAuthSession = (sessionData, makePersistent = null) => {
  try {
    const isApp = isAppEnvironment();
    const shouldPersist = makePersistent !== null ? makePersistent : isApp;

    const dataToSave = {
      ...sessionData,
      lastActivityAt: Date.now(),
      savedAt: Date.now()
    };

    const serialized = JSON.stringify(dataToSave);

    if (shouldPersist || isApp) {
      localStorage.setItem(PERSISTENT_SESSION_KEY, serialized);
      localStorage.setItem(SESSION_KEY, serialized);
      // Asynchronously mirror to Capacitor Preferences
      Preferences.set({ key: PERSISTENT_SESSION_KEY, value: serialized }).catch(() => {});
    } else {
      sessionStorage.setItem(SESSION_KEY, serialized);
    }
  } catch (e) {
    console.warn('[StorageService] Error saving session:', e);
  }
};

/**
 * Clears all auth sessions completely (upon manual logout).
 */
export const clearAuthSession = () => {
  try {
    sessionStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem('clou_auth_session');
    sessionStorage.removeItem('clou_access_token');
    
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(PERSISTENT_SESSION_KEY);
    localStorage.removeItem('clou_auth_session');
    localStorage.removeItem('clou_access_token');

    Preferences.remove({ key: PERSISTENT_SESSION_KEY }).catch(() => {});
    Preferences.remove({ key: SESSION_KEY }).catch(() => {});
  } catch (e) {
    console.warn('[StorageService] Error clearing session:', e);
  }
};
